import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { colors2, ButtonGhostCompact, OKELink } from "oolib";
import { UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION_DF } from "oolib/dist/v2/components/Typo2";
import { Sparkle } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "@/_self_managed/SM_Dashboard/comps/DashboardWidgetShell";
import { useAppSettingsContext } from "@/contexts/appSettingsContext";
import { StreamingAIMessage, useStreamedAskAI } from "@/components/AiChat/comps/Messages/StreamingAI";
import { AIMessage } from "@/components/AiChat/comps/Messages/AI";
import { SidebarProvider } from "@/components/AiChat/contexts/sidebarContext";
import { useGetChatById } from "@/components/AiChat/hooks";
import { SearchBar } from "@/components/AiChat/comps/SearchBar";
import { getDefaultAgentVersion } from "@/components/AiChat/agentVersion";
import { SuggestionsLanding } from "@/components/AiChat/comps/SuggestionsLanding";
import { useGetAiChatSuggestions } from "@/components/AiChat/hooks";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { buildSignalCategoryTheme } from "@/components/analysisModules/comps/SignalFilters/signalCategories";
import { resolveAskAICopy } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/copy";

/**
 * Ask AI on the dashboard: the tenant's suggested questions, and an answer
 * in place when one is picked.
 *
 * The questions come from GET /api/aiChat/suggestions and render through the
 * chat's own SuggestionsLanding, so both places offer the SAME questions from
 * the same source — a question is worth suggesting because of what the data
 * says, not because of which page you opened. This file owns only what is
 * particular to answering one here: the stream, the answer, and the card's
 * geometry.
 *
 * Three states, one card:
 *   idle      → the box and the suggestions
 *   streaming → the asked question and the agent's live trail (the same
 *               StreamingAIMessage the chat page renders)
 *   answered  → the stored message via AIMessage, so references, citations
 *               and feedback behave exactly as they do in the chat
 *
 * Reads its own scope rather than taking one: usePrioritiesDashboardScope is
 * the scope every Priorities surface on this tab uses, and react-query
 * answers each of its requests once — so scoring here costs nothing extra,
 * and the widget can sit anywhere on the page without being threaded data
 * through whatever happens to be above it.
 *
 * The stream runs the chat's own SSE endpoint against a NEW chat, so every
 * question asked here lands in the user's Ask AI history and can be picked
 * up there for follow-ups. No dashboard filters are sent: this tab scores
 * the whole corpus, and a narrowed answer would disagree with the widgets
 * beside it.
 */
const Composer = styled.div`
  flex-shrink: 0;
  min-width: 0;
`;
/**
 * The card's geometry, and the reason for it.
 *
 * An answer is unbounded — a paragraph or two pages, with tables, reference
 * cards and long unbroken ids. Left to size the card, it would grow the page
 * as it streams and shunt everything below it downward, token by token.
 *
 * So the card never sizes itself from its content: Frame is taken out of
 * flow (absolute, inset 0) and Scroll takes the overflow. The height comes
 * from the `height` prop instead — a sibling's row when it shares one, an
 * explicit figure when it runs the width alone.
 */
const Frame = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;
const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  /* Never sideways: a wide table or a long id wraps or clips rather than
     widening the column. */
  overflow-x: hidden;
  overflow-wrap: anywhere;

  display: flex;
  flex-direction: column;
  gap: 12px;

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  &:hover {
    scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
  }
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.25);
  }

  /* The chat's bubbles assume a chat column. Here they get the card. */
  & > * {
    max-width: 100%;
    min-width: 0;
  }
`;

export const AskAIWidget = ({ height = "100%" }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS?.lang?.value || "en";

  const [asked, setAsked] = useState(null);
  const [chatId, setChatId] = useState(null);
  const { stream, start, stop, reset, retry } = useStreamedAskAI();

  // The suggested questions: one cached request, the same the chat makes. The
  // mode gate keeps a legacy tenant from paying for a call that can only come
  // back { enabled: false } — the topic tree exists only on split tenants.
  const platformConfigs = useGetQueryData("platformConfigs");
  const isSplitTenant = getAnnoMode(platformConfigs) === "split";
  const { data: suggestions, status: suggestionsStatus } = useGetAiChatSuggestions({ enabled: isSplitTenant });
  const suggestionsEnabled = isSplitTenant && suggestionsStatus !== "error" && suggestions?.enabled !== false;
  const signalTheme = useMemo(
    () => buildSignalCategoryTheme(platformConfigs?.SIGNAL_CATEGORIES, { prioritiesCopy: suggestions?.prioritiesCopy }),
    [platformConfigs?.SIGNAL_CATEGORIES, suggestions?.prioritiesCopy]
  );

  // Once the answer is stored, swap the streamed text for the saved message —
  // that is the copy that carries references and the feedback actions.
  const { data: chatData } = useGetChatById({ chatId, enabled: !!chatId });
  const storedAnswer = useMemo(
    () => [...(chatData?.messages || [])].reverse().find((m) => m.author === "assistant"),
    [chatData]
  );

  const ask = (text) => {
    const query = (text || "").trim();
    if (!query || stream.active) return;
    setChatId(null);
    setAsked(query);
    start({
      query,
      chatId: "new",
      activeLang,
      aiChatAgent: getDefaultAgentVersion(),
      onComplete: (evt) => {
        setChatId(evt.chatId);
        // The question now has a chat of its own — let the Ask AI sidebar
        // know, so "continue in Ask AI" lands on a list that contains it.
        queryClient.invalidateQueries(["allChatsByThisUser"]);
      },
    });
  };

  const backToSuggestions = () => {
    reset();
    setAsked(null);
    setChatId(null);
  };

  return (
    <DashboardWidgetShell
      titleJSX={
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 0 }}>
          <Sparkle size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF>Ask AI</UI_BODY_SEMIBOLD_SM_DF>
        </div>
      }
      headerRight={
        asked ? (
          <ButtonGhostCompact S icon="ArrowLeft" value="Ask another" onClick={backToSuggestions} disabled={stream.active} />
        ) : null
      }
      style={{ height, minHeight: "45rem" }}
      bodyStyle={{ position: "relative", minHeight: 0 }}
    >
      <Frame>
        {/* Everything that can grow lives here and scrolls. */}
        <Scroll>
          {asked ? (
            <>
              <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80 }}>{asked}</UI_BODY_SEMIBOLD_SM_DF>
              {storedAnswer ? (
                <SidebarProvider>
                  <AIMessage message={storedAnswer} activeChatId={chatId} noBorderShadow directLinkReferences />
                </SidebarProvider>
              ) : (
                <StreamingAIMessage stream={stream} onStop={stop} onRetry={retry} autoScroll={false} />
              )}
              {chatId && (
                <UI_CAPTION_DF style={{ color: colors2.grey60 }}>
                  <OKELink onClick={() => navigate(`/ai-chat?chatId=${chatId}`)}>Open this chat in Ask AI</OKELink> to
                  follow up.
                </UI_CAPTION_DF>
              )}
            </>
          ) : (
            suggestionsEnabled && (
              <SuggestionsLanding
                data={suggestions}
                status={suggestionsStatus}
                theme={signalTheme}
                copy={resolveAskAICopy(suggestions?.prioritiesCopy, "dashboard")}
                // The card's own header already says what this is.
                showCopy={false}
                onPick={(card) => ask(card.text)}
                disabled={stream.active}
              />
            )
          )}
        </Scroll>

        {/* Pinned to the bottom, as on the Ask AI page — and it IS the Ask AI
            page's composer, so asking here and asking there feel the same.
            No deleteChat: there is no chat on this surface to clear. */}
        <Composer>
          <SearchBar
            onSubmit={({ value }) => ask(value)}
            disableOnSubmit={stream.active}
            placeholder="Ask anything about your data..."
          />
        </Composer>
      </Frame>
    </DashboardWidgetShell>
  );
};
